import { Box } from '@ltvco/refresh-lib/theme';
import { useState } from 'react';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';

import {
  COURT_CASES_V2_ROUTES,
  LEGAL_INTELLIGENCE_ROUTES,
  parseLegalIntelSection,
  withLegalIntelSection,
} from '../../../constants';
import { JudgeDetails } from '../../JudgeSearch';
import {
  useCourtCaseDetails,
  usePartiesAttorneysData,
  CourtCaseDisclaimer,
  InlineLoading,
  ErrorStateContainer,
} from '../../shared';
import { LegalSearchBreadcrumbs } from '../../shared/LegalSearchBreadcrumbs';

import { StyledTabsNavigation, StyledTab } from './components';
import { CourtCaseDetailsHeader } from './CourtCaseDetailsHeader';
import { CourtCaseDetailsIdbData } from './CourtCaseDetailsIdbData';
import { DocketEntries } from './DocketEntries';
import { PartiesAttorneys } from './PartiesAttorneys';

export const CaseDetails = () => {
  const { id } = useParams();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(0);
  const [docketEntriesCount, setDocketEntriesCount] = useState(0);
  const result = useCourtCaseDetails(id);
  const { docketQuery, courtQuery, isLoading } = result;
  const partiesAttorneysResult = usePartiesAttorneysData(id || '');
  const [searchParams] = useSearchParams();
  const assigned_to_id = searchParams.get('assigned_to_id');

  const {
    data: docketData,
    isError: isDocketError,
    error: docketError,
  } = docketQuery;
  const { data: courtData } = courtQuery;

  if (isLoading) {
    return <InlineLoading />;
  }

  if (isDocketError || !docketData) {
    return (
      <ErrorStateContainer>
        <p>
          Error:{' '}
          {docketError instanceof Error
            ? docketError.message
            : 'Docket not found'}
        </p>
      </ErrorStateContainer>
    );
  }

  const tabs = [
    {
      label: `Dockets (${docketEntriesCount})`,
      content: (
        <DocketEntries
          pacerCaseId={docketData?.pacer_case_id}
          pacerCourtId={courtData?.pacer_court_id}
          docketId={docketData.id.toString()}
          onCountChange={setDocketEntriesCount}
        />
      ),
    },
    {
      label: `Parties & Attorneys${
        partiesAttorneysResult.isLoading
          ? ' (...)'
          : ` (${partiesAttorneysResult.partiesCount})`
      }`,
      content: (
        <PartiesAttorneys partiesAttorneysResult={partiesAttorneysResult} />
      ),
    },
    ...(assigned_to_id && assigned_to_id !== 'null'
      ? [
          {
            label: 'Judge',
            content: <JudgeDetails judgeId={assigned_to_id} />,
          },
        ]
      : []),
    ...(docketData?.idb_data
      ? [
          {
            label: 'FJC Integrated Database',
            content: <CourtCaseDetailsIdbData docketData={docketData} />,
          },
        ]
      : []),
  ];

  const caseBreadcrumbTitle =
    docketData.case_name?.trim() || `Case ${docketData.id}`;

  const section = parseLegalIntelSection(location.search, location.pathname);

  return (
    <Box>
      <LegalSearchBreadcrumbs
        segments={[
          { label: 'Legal Intelligence', to: LEGAL_INTELLIGENCE_ROUTES.BASE },
          {
            label: 'Cases',
            to: withLegalIntelSection(COURT_CASES_V2_ROUTES.CASES, section),
          },
          { label: caseBreadcrumbTitle },
        ]}
      />
      <CourtCaseDetailsHeader
        selectedDocket={docketData}
        courtInfo={courtData || null}
      />

      <StyledTabsNavigation
        externalActiveTab={activeTab}
        onTabChanged={(newTab: number) => setActiveTab(newTab)}
        tab={StyledTab}
        tabs={tabs}
      />
      <CourtCaseDisclaimer />
    </Box>
  );
};
